import PublicApiService from "../../../../services/PublicApiService";
import { BadgeOrder, GetOrdersResponse } from "../../../../redux/types";
import { useDispatch, useStore } from "react-redux";
import { useRef } from "react";
import dayjs from "../../../../common/dayjs";
import { RootState } from "../../../../redux/store";
import { setBadgeOrders } from "../../../../redux/eventEdition/slice/eventEditionSlice";
import { badgeSyncRequestStarted, badgeSyncRequestCompleted, badgeSyncRequestFailed } from "redux/metadata/slice/metadataSlice";

export const useFetchBadgeOrders = () => {
  const dispatch = useDispatch();
  const badgeFetchInterval = useRef<NodeJS.Timer | null>(null);
  const { getState } = useStore();
  const orders = useRef<BadgeOrder[]>([]);

  const handlePagination = async (apiResponse: GetOrdersResponse, eventGbsCode: string) => {
    if (!apiResponse.token) {
      return;
    }

    const response = await PublicApiService.getBadgeOrders(eventGbsCode, apiResponse.token);
    orders.current = [...orders.current, ...response.results];
    await handlePagination(response, eventGbsCode);
  };

  const fetchBadgeOrders = async (eventGbsCode: string) => {
    dispatch(badgeSyncRequestStarted());
    try {
      const apiResponse = await PublicApiService.getBadgeOrders(eventGbsCode);
      orders.current = [...apiResponse.results];
      await handlePagination(apiResponse, eventGbsCode);
      dispatch(setBadgeOrders(orders.current));
      dispatch(badgeSyncRequestCompleted());
    } catch (e) {
      dispatch(badgeSyncRequestFailed());
      //  TODO: handle error
    }
  };

  const fetchAndReplaceOrders = async (eventGbsCode: string) => {
    try {
      const { eventEdition, metadata } = getState() as RootState;

      const apiResponse = await PublicApiService.getBadgeOrders(
        eventGbsCode,
        "",
        dayjs(metadata.lastBadgeUpdateTime).utc().format("YYYY-MM-DDTHH:mm:ss")
      );

      if (apiResponse.results?.length === 0) {
        return;
      }
      let ordersFromStore: BadgeOrder[] = [...eventEdition.orders];

      apiResponse.results.forEach(res => {
        const badgeOrders = ordersFromStore.filter(o => o.badgeId === res.badgeId && !o.isGeneratedLocal);
        ordersFromStore = [...ordersFromStore.filter(o => o.badgeId !== res.badgeId), ...badgeOrders];
      });

      dispatch(setBadgeOrders(ordersFromStore));
    } catch (e) {
      console.error(e);
    }
  };

  const startOrderFetchInterval = async (eventGbsCode: string) => {
    badgeFetchInterval.current = setInterval(() => {
      fetchAndReplaceOrders(eventGbsCode);
    }, 30000);
  };

  const clearBadgeFetchInterval = async () => {
    clearInterval(badgeFetchInterval.current!);
  };

  const recallForBadgeOrders = async (badgeId: string) => {
    try {
      return await PublicApiService.getBadgeOrdersByBadgeId(badgeId);
    } catch (error) {
      console.error("Error fetching badge orders:", error);
    }
  };

  return {
    fetchBadgeOrders,
    startOrderFetchInterval,
    clearBadgeFetchInterval,
    fetchAndReplaceOrders,
    recallForBadgeOrders
  };
};
